import type {
  GetInstructorsResult,
  GetInstructorsRequest,
} from '@features/instructors/api/get-instructors/getInstructors.types';
import type { UseQueryResult } from '@tanstack/react-query';

import { useQuery } from '@tanstack/react-query';

import type { UseInstructorsParams } from './useInstructors.types';

import { getInstructors } from '../../api/get-instructors/getInstructors';
import { buildSortParam } from './useInstructors.utils';

export function useInstructors(
  params: UseInstructorsParams
): UseQueryResult<GetInstructorsResult, Error> {
  const sortParam =
    params.sortBy && params.sortDirection
      ? buildSortParam(params.sortBy, params.sortDirection)
      : undefined;

  const apiParams: Partial<GetInstructorsRequest> = {
    top: params.pageSize,
    skip: params.pageIndex * params.pageSize,
  };

  if (params.searchText) apiParams.searchText = params.searchText;
  if (sortParam) apiParams.sort = sortParam;
  if (params.programs?.length) apiParams.programs = params.programs;
  if (params.locations?.length) apiParams.locations = params.locations;
  if (params.disciplines?.length) apiParams.disciplines = params.disciplines;

  return useQuery<GetInstructorsResult, Error>({
    queryKey: [
      'instructors',
      params.customerId,
      {
        pagination: {
          pageSize: params.pageSize,
          pageIndex: params.pageIndex,
        },
        sorting: {
          sortBy: params.sortBy ?? null,
          sortDirection: params.sortDirection ?? null,
        },
        searchText: params.searchText,
        programs: params.programs,
        locations: params.locations,
        disciplines: params.disciplines,
      },
    ],
    queryFn: (): Promise<GetInstructorsResult> =>
      getInstructors(params.customerId, apiParams),
    enabled: !!params.customerId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}
